// Builds the pages of the PDF saved by "Download PDF": a picture of the page's figures and charts exactly as they look on
// screen, under a heading that names the venue and the period that was chosen.
import { PAGE_RATIO } from './imagePdf';

const newCanvas = () => document.createElement('canvas');

// What is left over after the last cut stays on that sheet, a little smaller, if it is no more than this part of a sheet:
// an extra sheet holding only the space at the bottom of the page, or one small line, is worse than a slightly smaller sheet.
const TAIL = 0.1;

// A boundary this close to the top of a sheet (a hair under the cut that started it) is not a place to cut: it would make a sheet
// of a few pixels.
const SLIVER = 0.03;

// Writes text at the given size, made smaller only if it would not fit in `room`.
const writeFitted = (ctx, text, x, y, room, size, bold) => {
    ctx.font = `${bold ? 'bold ' : ''}${size}px sans-serif`;
    const width = ctx.measureText(text).width;
    if (width > room) ctx.font = `${bold ? 'bold ' : ''}${size * room / width}px sans-serif`;
    ctx.fillText(text, x, y);
};

// One tall white page: the heading, then the picture of the block at its own size. `captured` is one picture or several,
// drawn one under the other (the first is the main block of the page, the others are charts that follow it).
// `top` is how far down the page the first picture starts and `tops` where each one starts, so positions measured
// inside a block can be moved onto the page.
export function renderReportPage({ captured, venueName, reportName = 'browsing report', rangeText, createCanvas = newCanvas }) {
    const sections = Array.isArray(captured) ? captured : [captured];
    const widest = Math.max(...sections.map(section => section.width));
    const k = Math.max(widest / 900, 1); // text size follows the picture size
    const pad = 24 * k;
    const gap = 16 * k;
    const headingHeight = 64 * k;
    const canvas = createCanvas();
    canvas.width = widest + pad * 2;
    canvas.height = sections.reduce((sum, section) => sum + section.height, 0) + gap * (sections.length - 1) + headingHeight + pad * 2;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#24324d';
    writeFitted(ctx, `${venueName} - ${reportName}`, pad, pad + 22 * k, widest, 22 * k, true);
    ctx.fillStyle = '#6b7280';
    writeFitted(ctx, rangeText, pad, pad + 46 * k, widest, 14 * k, false);
    let y = pad + headingHeight;
    const tops = sections.map(section => {
        const top = y;
        ctx.drawImage(section, pad, top);
        y += section.height + gap;
        return top;
    });
    return { canvas, top: tops[0], tops };
}

// A page that fits on one A4 sheet stays whole. A taller one (a narrow window stacks everything) is cut into sheets,
// and each cut goes at the lowest content boundary (`breaks`, heights on the page where a block of the report ends) that
// still fits on the sheet, so a card or the chart is never cut in two, even if that leaves a sheet short. Only a block
// taller than a whole sheet, or no known boundaries, makes the cut fall at the sheet height.
// A sheet that starts inside the rows of a table (`repeats`: where its header is on the page and where the table ends) begins
// with a copy of that header, so the columns can still be read.
// A group that must stay together (`keeps`: where it starts and ends) is not cut inside if it fits on one sheet, even
// when it has boundaries of its own (the rows of a map); only a group taller than a sheet is cut between its rows.
export function splitIntoPages(page, breaks = [], createCanvas = newCanvas, repeats = [], keeps = []) {
    const sheetHeight = Math.floor(page.width * PAGE_RATIO);
    if (page.height <= sheetHeight) return [page];
    const whole = keeps.filter(group => group.end - group.start <= sheetHeight);
    breaks = breaks.filter(height => !whole.some(group => height > group.start && height < group.end));
    const sheets = [];
    let top = 0;
    while (top < page.height) {
        const from = top;
        // a table whose rows go on from here: its header is drawn first, so there is that much less room for the page itself
        const table = from > 0 ? repeats.find(item => from > item.headEnd && from < item.end && item.headEnd - item.headTop < sheetHeight / 3) : undefined;
        const headHeight = table ? table.headEnd - table.headTop : 0;
        const room = sheetHeight - headHeight;
        const limit = from + room;
        const fitting = breaks.filter(height => height > from + sheetHeight * SLIVER && height <= limit);
        const lastFit = fitting.length ? Math.max(...fitting) : from;
        // the block that runs over the end of the sheet starts where the last block that fits ends (if no later boundary is known, it is not known to be too tall)
        const nextBreak = breaks.filter(height => height > limit).reduce((least, height) => Math.min(least, height), Infinity);
        const overrunFits = nextBreak === Infinity || nextBreak - lastFit <= room;
        const cut = limit >= page.height ? page.height : lastFit > from && overrunFits ? lastFit : limit;
        const end = page.height - cut <= sheetHeight * TAIL ? page.height : cut;
        const height = end - top;
        const sheet = createCanvas();
        sheet.width = page.width;
        sheet.height = headHeight + height;
        const ctx = sheet.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, page.width, sheet.height);
        if (table) ctx.drawImage(page, 0, table.headTop, page.width, headHeight, 0, 0, page.width, headHeight);
        ctx.drawImage(page, 0, top, page.width, height, 0, headHeight, page.width, height);
        sheets.push(sheet);
        top = end;
    }
    return sheets;
}
